import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Checkbox, Grid, Tooltip, IconButton } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { Save, Delete } from '@mui/icons-material';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const EngineScoringClassificationHeaderComponent = ({
  scoringClassification,
  dictionaryScoringResponseState,
  dictionaryApplicationPage,
  engineClassificationIndex,
  dispatch,
  isReadOnly,
  onSave,
  collectionValidation
}) => {
  const { t } = useTranslation();
  const onPropertyChanged = (property, propertyValue) => {
    dispatch({
      type: 'OnClassificationHeaderChanged',
      property,
      propertyValue: propertyValue,
      classificationIndex: engineClassificationIndex
    });
  };

  const onSaveClassification = () => {
    onSave('OnSaveClassification', { scoringClassification, classificationIndex: engineClassificationIndex });
  };

  const onDeleteClassification = () => {
    if (window.confirm(t('EngineScoring.EngineClassifications.Delete'))) {
      onSave('OnDeleteClassification', scoringClassification, engineClassificationIndex);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={2} lg={1}>
        <TextField
          isNumeric
          fullWidth
          decimalScale={0}
          value={scoringClassification.orderNo}
          onChange={event => onPropertyChanged('orderNo', event)}
          disabled={isReadOnly}
          label={t('EngineScoring.EngineClassifications.OrderNo')}
          error={!isValid(collectionValidation[engineClassificationIndex].orderNo)}
          helperText={getErrors(collectionValidation[engineClassificationIndex].orderNo)}
        />
      </Grid>
      <Grid item xs={6} lg={4}>
        <TextField
          fullWidth
          value={scoringClassification.engineScoringClassificationName || ''}
          onChange={event => onPropertyChanged('engineScoringClassificationName', event)}
          label={t('EngineScoring.EngineClassifications.Name')}
          disabled={isReadOnly}
          error={!isValid(collectionValidation[engineClassificationIndex].engineScoringClassificationName)}
          helperText={getErrors(collectionValidation[engineClassificationIndex].engineScoringClassificationName)}
        />
      </Grid>
      <Grid item xs={6} sm={4} lg={2}>
        <Autocomplete
          fullWidth
          value={scoringClassification.displayPageId}
          simpleValue
          isDisabled={isReadOnly}
          options={dictionaryApplicationPage}
          onChange={event => onPropertyChanged('displayPageId', event)}
          label={t('EngineScoring.EngineClassifications.Page')}
        />
      </Grid>
      <Grid item xs={4} lg={2}>
        <Autocomplete
          fullWidth
          value={scoringClassification.linkedRuleId}
          simpleValue
          isDisabled={isReadOnly}
          options={dictionaryScoringResponseState}
          onChange={event => onPropertyChanged('linkedRuleId', event)}
          label={t('EngineScoring.EngineClassifications.LinkedRule')}
        />
      </Grid>
      <Grid item xs={2} sm={3} lg={1}>
        <FormControlLabel
          disabled={isReadOnly}
          control={
            <Checkbox
              checked={scoringClassification.visibleYN || false}
              onChange={event => onPropertyChanged('visibleYN', event.target.checked)}
              color='primary'
            />
          }
          label={t('EngineScoring.EngineClassifications.Visible')}
        />
      </Grid>
      <Grid container item xs={4} lg={2} justifyContent='flex-end'>
        <Grid item>
          <Tooltip title={t('Tooltips.EngineScoringGrid.DeleteClassification')}>
            <span>
              <IconButton onClick={onDeleteClassification} color='primary' disabled={isReadOnly}>
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip title={t('Tooltips.EngineScoringGrid.SaveClassification')}>
            <span>
              <IconButton
                onClick={onSaveClassification}
                color='primary'
                disabled={
                  isReadOnly ||
                  (collectionValidation |> getErrors) !== '' ||
                  !(collectionValidation[engineClassificationIndex] |> isValid) ||
                  !scoringClassification.changed ||
                  scoringClassification.saving
                }
              >
                <Save />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );
};

EngineScoringClassificationHeaderComponent.propTypes = {
  scoringClassification: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  engineClassificationIndex: PropTypes.number.isRequired,
  collectionValidation: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  dictionaryApplicationPage: PropTypes.array.isRequired,
  dictionaryScoringResponseState: PropTypes.array.isRequired
};

export default EngineScoringClassificationHeaderComponent;
